import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer, cartBoxCount } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #debouncedFlushBoxChanges = debounce(this.#flushBoxChanges, 200).bind(this);
  /** Box changes waiting to be sent, by product group: { delta, line }. */
  #pendingBoxChanges = new Map();
  /** The last box count each stepper reported, so every click is a delta on top of the previous one. */
  #reportedBoxes = new WeakMap();
  #flushingBoxChanges = false;
  #lastCart = null;
  #lastCartAt = 0;
  /** Price of one box in cents, by product group, learned from the cart (used to show new prices at once). */
  #boxPriceCents = new Map();
  #optimisticShown = false;
  #recommendationRequestId = 0;
  #recommendationQuantities = new Map();
  #cansPerBox = 8;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityEvent);
    this.addEventListener('click', this.#handleRecommendationClick);
    this.#initializeMoreItemsSection();
    this.#normalizeWaterLines();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityEvent);
    this.#debouncedFlushBoxChanges.cancel();
    this.removeEventListener('click', this.#handleRecommendationClick);
  }

  /**
   * Routes a stepper click. Water lines are queued right away (the number on the stepper already
   * changed, so the cart never waits or locks); other lines keep the standard debounced update.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #handleQuantityEvent = (event) => {
    const { quantity, cartLine: line } = event.detail;
    const input = event.target;
    const row = line ? this.#rowForLine(line) : undefined;

    if (!line || quantity === 0 || row?.dataset.customShopLine !== 'true' || !(input instanceof HTMLInputElement)) {
      this.#debouncedOnChange(event);
      return;
    }

    const previous = this.#reportedBoxes.get(input) ?? Number(input.defaultValue);
    this.#reportedBoxes.set(input, quantity);
    if (!Number.isFinite(previous) || previous === quantity) return;

    const groupKey = row.dataset.waterGroup || String(line);
    if (this.#showBoxesAtOnce(row, groupKey, quantity, previous)) this.#optimisticShown = true;
    const queued = this.#pendingBoxChanges.get(groupKey);
    this.#pendingBoxChanges.set(groupKey, { delta: (queued?.delta || 0) + (quantity - previous), line });
    this.#debouncedFlushBoxChanges();
  };

  /**
   * Sends the queued box changes. Clicks made while a request is running are folded into the next
   * request, and the cart is only redrawn once nothing is left to send, so the stepper never jumps
   * back to an older number.
   */
  async #flushBoxChanges() {
    if (this.#flushingBoxChanges) return;
    this.#flushingBoxChanges = true;
    this.#normalizing = true;
    if (!this.#optimisticShown) this.refs.cartTotal?.shimmer();

    let lastResult = null;

    try {
      while (this.#pendingBoxChanges.size > 0) {
        const changes = [...this.#pendingBoxChanges.entries()];
        this.#pendingBoxChanges.clear();

        for (const [groupKey, { delta, line }] of changes) {
          const cart = await this.#getCartForChange();
          const item = cart.items.find((candidate) => this.#waterGroupKey(candidate) === groupKey) || cart.items[line - 1];

          if (!item || !this.#isWaterLine(item)) throw new Error('Cart line is not a water line');

          const key = this.#waterGroupKey(item);
          const groupItems = cart.items.filter((candidate) => this.#isWaterLine(candidate) && this.#waterGroupKey(candidate) === key);
          const groupTotal = groupItems.reduce((sum, candidate) => sum + this.#lineBoxes(candidate), 0);
          const newTotal = Math.max(1, groupTotal + delta);

          if (newTotal === groupTotal) continue;

          const result = await this.#applyWaterGroup(groupItems, newTotal);
          this.#lastCart = result.cart?.items ? result.cart : null;
          this.#lastCartAt = Date.now();
          this.#rememberBoxPrices(result.cart);
          lastResult = result;
        }
      }

      if (lastResult?.cart?.sections) {
        await this.#renderFromCart(lastResult.cart);
      } else if (lastResult) {
        await this.#syncCartView();
      } else {
        resetShimmer(this);
      }
    } catch (error) {
      console.error(error);
      this.#lastCart = null;
      this.#pendingBoxChanges.clear();
      await this.#refreshFromServer();
    } finally {
      resetShimmer(this);
      this.#normalizing = false;
      this.#flushingBoxChanges = false;
      this.#optimisticShown = false;
      if (this.#pendingBoxChanges.size > 0) this.#flushBoxChanges();
    }
  }

  /**
   * Remembers what one box costs for each water group.
   * @param {object | null} cart - The cart JSON.
   */
  #rememberBoxPrices(cart) {
    (cart?.items || []).forEach((item) => {
      if (!this.#isWaterLine(item)) return;
      const boxes = this.#lineBoxes(item);
      if (boxes > 0) this.#boxPriceCents.set(this.#waterGroupKey(item), Math.round(Number(item.final_line_price) / boxes));
    });
  }

  /**
   * Shows the new box count, line price and totals the moment a stepper is tapped, so the cart
   * does not wait for the server. The real values replace these when the redraw arrives.
   * Skipped (returns false) whenever the markup or the numbers are not the simple single-line case.
   * @param {HTMLTableRowElement} row - The row of the tapped stepper.
   * @param {string} groupKey - The product group of the row.
   * @param {number} quantity - The new box count of the line.
   * @param {number} previous - The box count shown before the tap.
   * @returns {boolean} Whether the numbers were updated.
   */
  #showBoxesAtOnce(row, groupKey, quantity, previous) {
    const boxPrice = this.#boxPriceCents.get(groupKey);
    if (!boxPrice || quantity < 1 || previous < 1 || quantity > 12) return false;

    const rowsInGroup = this.refs.cartItemRows.filter((candidate) => candidate.dataset.waterGroup === groupKey);
    if (rowsInGroup.length !== 1) return false;

    const packParts = row.querySelectorAll('.cart-items__media-pack > span');
    const unitPrice = row.querySelector('.cart-items__line-price > span:not(.visually-hidden)');
    const linePrice = row.querySelector('text-component');
    const cell = linePrice?.parentElement;
    if (packParts.length < 2 || !unitPrice || !linePrice) return false;

    const summaries = [...this.querySelectorAll('.cart__total-container .shop-switcher__summary')].map((summary) => {
      const rows = summary.querySelectorAll('.shop-switcher__summary-row');
      const labelRow = rows[0]?.querySelectorAll('span');
      const shipping = summary.querySelector('.cart__shipping-value');
      const total = summary.querySelector('.cart__total-value');
      return { summary, label: labelRow?.[0], subtotal: labelRow?.[1], shipping, total };
    });
    if (summaries.length === 0 || summaries.some((parts) => !parts.label || !parts.subtotal || !parts.total)) return false;

    const parseMoney = (text) => {
      const match = /\$([\d,]+\.\d{2})/.exec(text || '');
      return match ? Math.round(Number(match[1].replace(/,/g, '')) * 100) : null;
    };
    const formatMoney = (cents) => `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const swapMoney = (element, cents) => {
      element.textContent = (element.textContent || '').replace(/\$[\d,]+\.\d{2}/, formatMoney(cents));
    };
    const boxesLabel = (count) => `${count} box${count === 1 ? '' : 'es'}`;
    const currencySuffix = (element) => /\$[\d,]+\.\d{2}(.*)$/.exec((element.textContent || '').trim())?.[1] ?? '';
    const delta = quantity - previous;
    const cartItems = this.#lastCart?.items || [];
    const thresholdSetting = Number(cartItems.find((item) => item.properties?._custom_shop_free_shipping_threshold)?.properties?._custom_shop_free_shipping_threshold);
    const freeAt = Number.isFinite(thresholdSetting) && thresholdSetting > 0 ? thresholdSetting : 2;
    const shippingSetting = Number(cartItems.find((item) => item.properties?._custom_shop_shipping_cents)?.properties?._custom_shop_shipping_cents);
    const shippingCents = Number.isFinite(shippingSetting) && shippingSetting > 0 ? shippingSetting : 867;

    const updates = [];
    for (const parts of summaries) {
      const boxesNow = Number(/(\d+)\s+box/.exec(parts.label.textContent || '')?.[1]);
      const subtotalNow = parseMoney(parts.subtotal.textContent);
      if (!boxesNow || subtotalNow === null) return false;
      updates.push({ parts, boxes: boxesNow + delta, subtotal: subtotalNow + delta * boxPrice });
    }

    const lineCents = quantity * boxPrice;
    const cansText = `(${quantity * this.#cansPerBox} cans)`;
    packParts[0].textContent = `${quantity} Box${quantity === 1 ? '' : 'es'}`;
    packParts[1].textContent = cansText;
    unitPrice.textContent = formatMoney(lineCents);
    linePrice.setAttribute('value', formatMoney(lineCents));
    linePrice.textContent = formatMoney(lineCents);
    cell?.querySelector('.cart-items__line-pack:not(.cart-items__line-pack--breakdown)')?.remove();
    this.#showBreakdown(linePrice, quantity, boxPrice, formatMoney, cansText);

    updates.forEach(({ parts, boxes, subtotal }) => {
      const free = boxes >= freeAt;
      parts.label.textContent = `${boxesLabel(boxes)} (${boxes * this.#cansPerBox} cans)`;
      swapMoney(parts.subtotal, subtotal);
      if (parts.shipping) parts.shipping.textContent = free ? 'Free' : `${formatMoney(shippingCents)}${currencySuffix(parts.subtotal)}`;
      const totalCents = subtotal + (free ? 0 : shippingCents);
      parts.total.setAttribute('value', (parts.total.getAttribute('value') || '').replace(/\$[\d,]+\.\d{2}/, formatMoney(totalCents)));
      swapMoney(parts.total, totalCents);
    });

    // The number on the cart icon and the cart heading counts boxes too.
    document.querySelectorAll('.cart-bubble__text-count').forEach((count) => {
      const shown = Number(count.textContent);
      if (Number.isFinite(shown) && count.textContent?.trim()) count.textContent = String(Math.max(0, shown + delta));
    });

    return true;
  }

  /**
   * Shows "$11.99 x N Boxes (M cans)" before the line total, the way the server draws every water line.
   * @param {Element} linePrice - The line total inside the price cell.
   * @param {number} quantity - The new box count of the line.
   * @param {number} boxPrice - The price of one box in cents.
   * @param {(cents: number) => string} formatMoney - Formats cents as money.
   * @param {string} cansText - The "(N cans)" text.
   */
  #showBreakdown(linePrice, quantity, boxPrice, formatMoney, cansText) {
    const cell = linePrice.parentElement;
    if (!cell) return;

    const amountText = `${formatMoney(boxPrice)} x ${quantity} Box${quantity === 1 ? '' : 'es'}`;
    const breakdown = cell.querySelector('.cart-items__line-pack--breakdown');

    if (breakdown) {
      const [amount, cans] = breakdown.querySelectorAll(':scope > span');
      if (amount) amount.textContent = amountText;
      if (cans) cans.textContent = cansText;
      return;
    }

    const created = document.createElement('span');
    created.className = 'cart-items__line-pack cart-items__line-pack--breakdown';
    created.append(document.createElement('span'), document.createElement('span'));
    created.children[0].textContent = amountText;
    created.children[1].textContent = cansText;

    const divider = document.createElement('span');
    divider.className = 'cart-items__price-divider';
    divider.setAttribute('aria-hidden', 'true');
    linePrice.before(created, divider);
  }

  /**
   * The cart as the last box change left it, or a fresh copy when that is stale.
   * @returns {Promise<object>} The cart JSON.
   */
  async #getCartForChange() {
    if (this.#lastCart && Date.now() - this.#lastCartAt < 5000) return this.#lastCart;

    this.#lastCart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    this.#lastCartAt = Date.now();
    this.#rememberBoxPrices(this.#lastCart);
    return this.#lastCart;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.#rowForLine(line);

    // Water lines count BOXES: re-split the new box count into the matching size variants.
    if (lineItemRow?.dataset.customShopLine === 'true') {
      this.#changeCustomLineBoxes(line, quantity);
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Redraws the cart section while keeping the "More Items You'll Love" cards on screen.
   * The server's HTML has no cards (they are drawn by script), so a plain redraw would wipe them
   * for a second and then bring them back, which looked like a flash and made the page jump.
   * @param {string} sectionId - The section id.
   * @param {string} html - The new section HTML.
   */
  async #morphKeepingCards(sectionId, html) {
    const savedHtml = this.querySelector('#cart-recommendations')?.innerHTML || '';
    const savedDisplay = this.querySelector('.more-items-you-ll-love')?.style.display || '';

    await morphSection(sectionId, html);

    const container = this.querySelector('#cart-recommendations');
    if (container && savedHtml.trim() && !container.innerHTML.trim()) {
      container.innerHTML = savedHtml;
      const section = this.querySelector('.more-items-you-ll-love');
      if (section) section.style.display = savedDisplay;
    }
  }

  /**
   * Finds the row of a cart line by its line number (rows are not always in cart order).
   * @param {number} line - The 1-indexed cart line.
   * @returns {HTMLTableRowElement | undefined} The row.
   */
  #rowForLine(line) {
    return this.refs.cartItemRows.find((row) => row.dataset.line === String(line)) || this.refs.cartItemRows[line - 1];
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#rowForLine(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  #normalizing = false;
  #sizedVariantsCache = new Map();

  /**
   * Whether a cart line is a water line added by the shop (not the legacy shipping fee line).
   * @param {object} item - The cart item.
   * @returns {boolean} True for water lines.
   */
  #isWaterLine(item) {
    const props = item?.properties || {};
    if (props._custom_shop === 'true') return props._custom_shop_fee !== 'shipping';

    // Plain water lines (for example from a Buy again link) are sized the same way.
    return /still-water|sparkling-water/.test(String(item?.handle || ''));
  }

  /**
   * Boxes in a water line: boxes per unit times quantity.
   * @param {object} item - The cart item.
   * @returns {number} The boxes.
   */
  #lineBoxes(item) {
    const unitBoxes =
      Number(item?.properties?._custom_shop_boxes || 0) || this.#extractBoxCount(item?.variant_title || '') || 1;
    return Number(item?.quantity || 0) * unitBoxes;
  }

  /**
   * Lines of the same product and purchase type form one group that is sized as a whole.
   * @param {object} item - The cart item.
   * @returns {string} The group key.
   */
  #waterGroupKey(item) {
    return `${item.handle}|${item.selling_plan_allocation ? 'subscription' : 'one-time'}`;
  }

  /**
   * Gets a product's sized variants (id, boxes, availability), cached per product.
   * @param {string} handle - The product handle.
   * @returns {Promise<Array<{id: number, boxes: number, available: boolean}>>} The sized variants.
   */
  async #getSizedVariants(handle) {
    if (this.#sizedVariantsCache.has(handle)) return this.#sizedVariantsCache.get(handle);

    const product = await this.#fetchRecommendedProduct(handle);
    const variants = (Array.isArray(product?.variants) ? product.variants : [])
      .map((variant) => ({
        id: Number(variant.id),
        boxes: this.#getVariantBoxCount(variant) || 0,
        available: variant.available !== false,
      }))
      .filter((variant) => variant.id && variant.boxes > 0);

    if (variants.length > 0) this.#sizedVariantsCache.set(handle, variants);
    return variants;
  }

  /**
   * Makes the cart lines of one group equal the matching split of a total box count
   * (for example 5 boxes is one "5 Boxes" line, 13 boxes is "12 Boxes" plus "1 Box").
   * @param {Array<object>} groupItems - The cart items of the group.
   * @param {number} newTotal - The total boxes the group should hold.
   * @returns {Promise<{changed: boolean, cart: object | null}>} Whether the cart changed, and the cart
   *   (with redrawn sections) returned by the last request when it carried them.
   */
  async #applyWaterGroup(groupItems, newTotal) {
    const template = groupItems[0];
    const variants = await this.#getSizedVariants(template.handle);
    if (variants.length === 0) return { changed: false, cart: null };

    const combination = this.#splitIntoVariants(variants, newTotal, Number(template.variant_id));
    const planId = template.selling_plan_allocation?.selling_plan?.id;
    const isSubscription = Boolean(template.selling_plan_allocation);
    // Plain lines get the same bookkeeping properties the shop adds itself.
    const props =
      template.properties?._custom_shop === 'true'
        ? template.properties
        : this.#buildRecommendationCartItem({
            variantId: Number(template.variant_id),
            quantity: 1,
            unitBoxes: 1,
            purchaseType: isSubscription ? 'subscription' : 'one-time',
            waterType: String(template.handle || '').includes('sparkling') ? 'sparkling' : 'still',
            sellingPlanId: planId,
            sellingPlanName: template.selling_plan_allocation?.selling_plan?.name || '',
          }).properties;
    const isShopLine = (item) => item.properties?._custom_shop === 'true';
    const present = new Set(groupItems.filter(isShopLine).map((item) => Number(item.variant_id)));
    const partsToAdd = combination.filter((part) => !present.has(part.variantId));
    let changed = false;

    if (partsToAdd.length > 0) {
      const response = await this.#addCartItems(
        partsToAdd.map((part) => ({
          id: part.variantId,
          quantity: part.quantity,
          ...(planId ? { selling_plan: Number(planId) } : {}),
          properties: {
            ...props,
            _custom_shop_boxes: String(part.unitBoxes),
            _custom_shop_variant_label: `${part.unitBoxes} ${part.unitBoxes === 1 ? 'Box' : 'Boxes'} (${part.unitBoxes * this.#cansPerBox} cans)`,
          },
        })),
        []
      );

      if (!response.ok) throw new Error('Could not add the new size');
      changed = true;
    }

    // Existing lines keep their share of the new split; extra lines of the same size are emptied.
    const updates = {};
    const claimed = new Set();
    groupItems.forEach((item) => {
      const part = isShopLine(item) ? combination.find((candidate) => candidate.variantId === Number(item.variant_id)) : null;
      const wanted = part && !claimed.has(part.variantId) ? part.quantity : 0;
      if (part) claimed.add(part.variantId);
      if (wanted !== Number(item.quantity)) updates[item.key] = wanted;
    });

    let cart = null;

    if (Object.keys(updates).length > 0) {
      const response = await fetch(
        `${Theme.routes.cart_update_url}.js`,
        fetchConfig('json', {
          body: JSON.stringify({
            updates,
            sections: this.#getCartSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      if (!response.ok) throw new Error('Could not update the cart lines');
      cart = await response.json();
      changed = true;
    }

    return { changed, cart };
  }

  /**
   * Shows a cart that a request just returned (with its redrawn sections) and tells the page it changed.
   * @param {object} cart - The cart JSON, including `sections`.
   */
  async #renderFromCart(cart) {
    let sections = cart.sections || {};
    this.#rememberBoxPrices(cart);

    // The old shipping fee line only exists on carts made before flat rates, so skip the extra request.
    if ((cart.items || []).some((item) => item.properties?._custom_shop_fee === 'shipping')) {
      const syncedShippingSections = await this.#removeShippingFeeIfFreeShipping();
      if (syncedShippingSections) sections = syncedShippingSections;
    }

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: cartBoxCount(cart.items),
        source: 'cart-items-component',
        sections,
      })
    );

    if (sections[this.sectionId]) await this.#morphKeepingCards(this.sectionId, sections[this.sectionId]);
    this.#updateMoreItemsSection(cart.items);
  }

  /**
   * Redraws the cart from the server and tells the rest of the page the cart changed.
   */
  async #syncCartView() {
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    this.#rememberBoxPrices(cart);
    const sectionIds = this.#getCartSectionIds();
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));

    let sections = await fetch(url.toString()).then((response) => response.json());

    if ((cart.items || []).some((item) => item.properties?._custom_shop_fee === 'shipping')) {
      const syncedShippingSections = await this.#removeShippingFeeIfFreeShipping();
      if (syncedShippingSections) sections = syncedShippingSections;
    }

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount: cartBoxCount(cart.items),
        source: 'cart-items-component',
        sections,
      })
    );

    if (sections[this.sectionId]) await this.#morphKeepingCards(this.sectionId, sections[this.sectionId]);
    this.#updateMoreItemsSection(cart.items);
  }

  /**
   * Makes sure every water group in the cart is sized the matching way, whatever added the lines
   * (purchase box, cart cards, repeat adds). Does nothing when the cart is already right.
   */
  async #normalizeWaterLines() {
    if (this.#normalizing) return;
    this.#normalizing = true;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      const groups = new Map();
      this.#rememberBoxPrices(cart);

      (cart.items || []).forEach((item) => {
        if (!this.#isWaterLine(item)) return;
        const key = this.#waterGroupKey(item);
        groups.set(key, [...(groups.get(key) || []), item]);
      });

      let changed = false;

      for (const items of groups.values()) {
        const total = items.reduce((sum, item) => sum + this.#lineBoxes(item), 0);
        const variants = await this.#getSizedVariants(items[0].handle);
        if (variants.length === 0 || total < 1) continue;

        const combination = this.#splitIntoVariants(variants, total, Number(items[0].variant_id));
        const alreadyRight =
          items.every((item) => item.properties?._custom_shop === 'true') &&
          items.length === combination.length &&
          combination.every((part) => items.some((item) => Number(item.variant_id) === part.variantId && Number(item.quantity) === part.quantity));

        if (!alreadyRight) {
          changed = (await this.#applyWaterGroup(items, total)).changed || changed;
        }
      }

      if (changed) await this.#syncCartView();
    } catch (error) {
      console.error(error);
    } finally {
      this.#normalizing = false;
    }
  }

  /**
   * Changes a water line to a new number of boxes. The product's lines are then re-split as a whole
   * so every line still reads "5 Boxes (40 Cans)", never "1 Box x 5".
   * @param {number} line - The 1-indexed cart line.
   * @param {number} targetBoxes - The new total boxes for that line.
   */
  async #changeCustomLineBoxes(line, targetBoxes) {
    const cartPerformanceMarker = cartPerformance.createStartingMarker('change:user-action');
    const { cartTotal } = this.refs;

    this.#disableCartItems();
    cartTotal?.shimmer();
    this.#normalizing = true;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      const item = cart.items?.[line - 1];

      if (!item || !this.#isWaterLine(item)) throw new Error('Cart line is not a water line');

      const groupKey = this.#waterGroupKey(item);
      const groupItems = cart.items.filter((candidate) => this.#isWaterLine(candidate) && this.#waterGroupKey(candidate) === groupKey);
      const groupTotal = groupItems.reduce((sum, candidate) => sum + this.#lineBoxes(candidate), 0);
      const newTotal = Math.max(1, groupTotal - this.#lineBoxes(item) + Math.max(1, Math.round(Number(targetBoxes) || 1)));

      const result = newTotal !== groupTotal ? await this.#applyWaterGroup(groupItems, newTotal) : { changed: false, cart: null };

      if (result.cart?.sections) {
        await this.#renderFromCart(result.cart);
      } else {
        await this.#syncCartView();
      }
    } catch (error) {
      console.error(error);
      await this.#refreshFromServer();
    } finally {
      resetShimmer(this);
      this.#normalizing = false;
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceMarker);
    }
  }

  /**
   * Re-renders the cart section from the server (used after a failed change).
   */
  async #refreshFromServer() {
    try {
      const sectionIds = this.#getCartSectionIds();
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sectionIds.join(','));
      const sections = await fetch(url.toString()).then((response) => response.json());
      if (sections?.[this.sectionId]) await morphSection(this.sectionId, sections[this.sectionId]);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const syncedShippingSections = await this.#removeShippingFeeIfFreeShipping();
        const sections = syncedShippingSections || parsedResponseText.sections;

        // Dispatch CartUpdateEvent with the updated sections
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: cartBoxCount(parsedResponseText.items),
            source: 'cart-items-component',
            sections,
          })
        );

        this.#morphKeepingCards(this.sectionId, sections[this.sectionId]);
        this.#updateMoreItemsSection(parsedResponseText.items);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the "More Items You'll Love" section based on cart contents.
   * Tracks 4 variations (still/sparkling x one-time/subscription) and
   * recommends whichever combinations are not yet in the cart. The section
   * (including its heading) hides only when all 4 are present.
   * @param {Array} cartItems - The array of items currently in the cart.
   */
  #updateMoreItemsSection(cartItems) {
    const moreItemsSection = this.querySelector('.more-items-you-ll-love');
    if (!moreItemsSection) return;

    const recommendationsContainer = moreItemsSection.querySelector('#cart-recommendations');
    if (!(recommendationsContainer instanceof HTMLElement)) return;

    const dataset = moreItemsSection.dataset;
    const stillHandle = dataset.stillHandle || '';
    const sparklingHandle = dataset.sparklingHandle || '';

    const combinations = [
      {
        water: 'still',
        purchase: 'one-time',
        handle: stillHandle,
        variantId: Number(dataset.stillVariantId || 0) || null,
        sellingPlanId: null,
        sellingPlanName: '',
      },
      {
        water: 'sparkling',
        purchase: 'one-time',
        handle: sparklingHandle,
        variantId: Number(dataset.sparklingVariantId || 0) || null,
        sellingPlanId: null,
        sellingPlanName: '',
      },
      {
        water: 'still',
        purchase: 'subscription',
        handle: stillHandle,
        variantId: Number(dataset.stillVariantId || 0) || null,
        sellingPlanId: Number(dataset.stillSellingPlanId || 0) || null,
        sellingPlanName: dataset.stillSellingPlanName || '',
      },
      {
        water: 'sparkling',
        purchase: 'subscription',
        handle: sparklingHandle,
        variantId: Number(dataset.sparklingVariantId || 0) || null,
        sellingPlanId: Number(dataset.sparklingSellingPlanId || 0) || null,
        sellingPlanName: dataset.sparklingSellingPlanName || '',
      },
    ].filter((combo) => combo.handle);

    const presentKeys = new Set();
    (cartItems || []).forEach((item) => {
      const key = this.#getCartWaterKey(item, stillHandle, sparklingHandle);
      if (key) presentKeys.add(key);
    });

    const missing = combinations.filter((combo) => !presentKeys.has(`${combo.water}|${combo.purchase}`));

    if (missing.length === 0) {
      this.#recommendationRequestId += 1;
      recommendationsContainer.innerHTML = '';
      moreItemsSection.style.display = 'none';
      return;
    }

    // The cards on screen are already the right ones: leave them alone (no redraw, no flash).
    const shownKeys = Array.from(recommendationsContainer.querySelectorAll('[data-cart-recommendation]'))
      .map((card) => `${card.dataset.waterType}|${card.dataset.purchaseType}`)
      .sort()
      .join(',');
    const wantedKeys = missing.map((combo) => `${combo.water}|${combo.purchase}`).sort().join(',');

    if (shownKeys === wantedKeys) {
      moreItemsSection.style.display = 'block';
      return;
    }

    const requestId = ++this.#recommendationRequestId;
    const handlesToFetch = Array.from(new Set(missing.map((combo) => combo.handle)));

    Promise.all(handlesToFetch.map((handle) => this.#fetchRecommendedProduct(handle).then((product) => ({ handle, product }))))
      .then((results) => {
        if (requestId !== this.#recommendationRequestId) return;

        const productsByHandle = new Map(results.map(({ handle, product }) => [handle, product]));
        const cards = [];

        missing.forEach((combo) => {
          const product = productsByHandle.get(combo.handle);
          if (!product) return;
          const card = this.#renderRecommendationCard(product, combo.purchase, combo);
          if (card) cards.push(card);
        });

        if (cards.length > 0) {
          recommendationsContainer.innerHTML = cards.join('');
          moreItemsSection.style.display = 'block';
        } else {
          recommendationsContainer.innerHTML = '';
          moreItemsSection.style.display = 'none';
        }
      })
      .catch((error) => {
        console.error('Error fetching product recommendations:', error);
        if (requestId !== this.#recommendationRequestId) return;

        recommendationsContainer.innerHTML = '';
        moreItemsSection.style.display = 'none';
      });
  }

  /**
   * Identifies which of the 4 water variations a cart line represents.
   * Custom-shop lines use their purchase-type property/selling plan;
   * plain water lines without properties count as one-time.
   * @param {object} item - The cart item.
   * @param {string} stillHandle - The still product handle.
   * @param {string} sparklingHandle - The sparkling product handle.
   * @returns {string|null} A "{water}|{purchase}" key, or null when not a water item.
   */
  #getCartWaterKey(item, stillHandle, sparklingHandle) {
    const props = item?.properties || {};

    if (props._custom_shop_fee === 'shipping') return null;

    let water = null;
    const customHandle = String(props._custom_shop_handle || '').toLowerCase();
    if (customHandle === 'still') {
      water = 'still';
    } else if (customHandle === 'sparkling') {
      water = 'sparkling';
    } else {
      const itemHandle =
        item.handle ||
        item.url?.split('/products/')[1]?.split(/[?#]/)[0] ||
        '';
      const title = String(item.product_title || item.title || '').toLowerCase();
      const handleLower = String(itemHandle).toLowerCase();

      const isStill =
        handleLower === String(stillHandle).toLowerCase() ||
        handleLower.includes('still-water') ||
        title.includes('still water');
      const isSparkling =
        handleLower === String(sparklingHandle).toLowerCase() ||
        handleLower.includes('sparkling-water') ||
        title.includes('sparkling water');

      if (isStill && !isSparkling) water = 'still';
      else if (isSparkling && !isStill) water = 'sparkling';
      else return null;
    }

    const isSubscription =
      props._custom_shop_purchase_type === 'subscription' ||
      item?.selling_plan_allocation != null;

    return `${water}|${isSubscription ? 'subscription' : 'one-time'}`;
  }

  /**
   * Populates the recommendation section on initial load when the container exists.
   */
  #initializeMoreItemsSection() {
    if (!this.querySelector('.more-items-you-ll-love')) return;

    fetch(`${Theme.routes.cart_url}.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Cart request failed with status ${response.status}`);
        }

        return response.json();
      })
      .then((cart) => {
        this.#updateMoreItemsSection(cart.items || []);
      })
      .catch((error) => {
        console.error('Error initializing recommended products:', error);
      });
  }

  /**
   * Fetches a product by handle from Shopify's product JSON endpoint.
   * @param {string} handle - The product handle.
   * @returns {Promise<object | null>} The product payload, if found.
   */
  #fetchRecommendedProduct(handle) {
    const rootPath = window.Shopify?.routes?.root || '/';

    return fetch(`${rootPath}products/${encodeURIComponent(handle)}.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Recommendation request failed with status ${response.status}`);
        }

        return response.json();
      })
      .catch((error) => {
        console.error(`Error fetching recommended product "${handle}":`, error);
        return null;
      });
  }

  /**
   * Builds the cart drawer recommendation card markup.
   * Recommendation quantities stay independent from live cart line quantities.
   * @param {object} product - The Shopify product payload.
   * @param {string} [purchaseType] - "one-time" or "subscription".
   * @param {object} [combo] - The variation metadata (variant/selling-plan ids).
   * @returns {string} The recommendation card HTML.
   */
  #renderRecommendationCard(product, purchaseType = 'one-time', combo = {}) {
    const imageUrl = product.featured_image || product.images?.[0] || '';
    const title = this.#escapeHtml(product.title || '');
    const recommendationVariant = this.#getRecommendationVariant(product, combo.variantId);
    const normalizedPurchase = purchaseType === 'subscription' ? 'subscription' : 'one-time';
    const minQuantity = normalizedPurchase === 'subscription' ? 2 : 1;
    const storedQuantity = this.#getStoredRecommendationQuantity(product, purchaseType);
    const initialQuantity = Math.max(minQuantity, storedQuantity);

    if (!recommendationVariant) return '';

    const linePrice = this.#formatRecommendationPrice(Number(recommendationVariant.price || 0) * initialQuantity);
    const purchaseLabel = normalizedPurchase === 'subscription' ? 'Subscription (Every 2 weeks)' : 'One Time';
    const packBoxes = `${initialQuantity} ${initialQuantity === 1 ? 'Box' : 'Boxes'}`;
    const packCans = `(${initialQuantity * this.#cansPerBox} Cans)`;
    const imageMarkup = imageUrl
      ? `<img src="${imageUrl}" alt="${title}" width="250" height="250" loading="lazy" decoding="async" class="product-recommendation-card__image">`
      : '';
    const note = this.#getRecommendationNote(product);
    const noteMarkup = note
      ? `
        <div class="cart-items__error product-recommendation-card__note-container" role="note">
          <p class="product-recommendation-card__note">${this.#escapeHtml(note)}</p>
        </div>
      `
      : '';
    const waterType = combo.water || (product.handle?.includes('sparkling') ? 'sparkling' : 'still');
    const sellingPlanId = Number(combo.sellingPlanId || 0) || '';

    return `
      <div
        role="row"
        class="cart-items__table-row product-recommendation-card"
        data-cart-recommendation
        data-variant-id="${recommendationVariant.id}"
        data-unit-price="${recommendationVariant.price}"
        data-product-id="${product.id}"
        data-product-handle="${this.#escapeHtml(product.handle || '')}"
        data-purchase-type="${normalizedPurchase}"
        data-water-type="${this.#escapeHtml(waterType)}"
        data-selling-plan-id="${sellingPlanId}"
        data-selling-plan-name="${this.#escapeHtml(combo.sellingPlanName || '')}"
        data-boxes-per-unit="1"
        data-variants="${this.#escapeHtml(this.#serializeRecommendationVariants(product))}"
      >
        <div class="cart-items__media" role="cell" headers="productImage">
          <div class="cart-items__media-container product-recommendation-card__media-link" style="--ratio:1;">
            ${imageMarkup}
          </div>
          <span class="cart-items__media-pack" data-recommendation-pack><span data-recommendation-pack-boxes>${packBoxes}</span><span data-recommendation-pack-cans>${packCans}</span></span>
        </div>
        <div class="cart-items__details cart-primary-typography" role="cell" headers="productInformation">
          <p>
            <span class="cart-items__title">${title}<br><span class="cart-items__purchase-type">${this.#escapeHtml(purchaseLabel)}</span></span>
          </p>
        </div>
        <div class="cart-items__quantity product-recommendation-card__actions" role="cell" headers="quantity" aria-label="Recommended product quantity">
          <div class="quantity-selector cart-primary-typography product-recommendation-card__quantity">
            <button
              class="button quantity-minus button-unstyled product-recommendation-card__quantity-button"
              type="button"
              data-recommendation-action="decrement"
              aria-label="Decrease quantity"
            >
              <span class="visually-hidden">Decrease quantity</span>
              <span class="svg-wrapper icon-plus">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                </svg>
              </span>
            </button>
            <input
              type="number"
              value="${initialQuantity}"
              min="${minQuantity}"
              readonly
              inputmode="numeric"
              aria-label="Quantity"
              class="product-recommendation-card__quantity-input"
              data-recommendation-quantity-input
            >
            <button
              class="button quantity-plus button-unstyled product-recommendation-card__quantity-button"
              type="button"
              data-recommendation-action="increment"
              aria-label="Increase quantity"
            >
              <span class="visually-hidden">Increase quantity</span>
              <span class="svg-wrapper icon-plus">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                  <path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
                </svg>
              </span>
            </button>
          </div>
          <button type="button" class="button button--tertiary cart-items__remove product-recommendation-card__add-button" data-recommendation-action="add">
            Add
          </button>
        </div>
        <div class="cart-items__price cart-secondary-typography product-recommendation-card__price" role="cell" headers="productTotal">
          <text-component data-recommendation-line-price value="${linePrice}">${linePrice}</text-component>
        </div>
        ${noteMarkup}
      </div>
    `;
  }

  /**
   * Handles clicks on the recommendation quantity and add controls.
   * @param {MouseEvent} event - The click event.
   */
  #handleRecommendationClick = (event) => {
    const actionTrigger = event.target instanceof Element
      ? event.target.closest('[data-recommendation-action]')
      : null;

    if (!(actionTrigger instanceof HTMLElement)) return;

    const recommendationCard = actionTrigger.closest('[data-cart-recommendation]');
    if (!(recommendationCard instanceof HTMLElement)) return;

    event.preventDefault();

    const action = actionTrigger.dataset.recommendationAction;
    const currentQuantity = this.#getRecommendationQuantity(recommendationCard);

    if (action === 'increment') {
      this.#setRecommendationQuantity(recommendationCard, currentQuantity + 1);
      return;
    }

    if (action === 'decrement') {
      this.#setRecommendationQuantity(recommendationCard, currentQuantity - 1);
      return;
    }

    if (action === 'add') {
      const variantId = Number(recommendationCard.dataset.variantId);
      if (!variantId) return;

      this.#addRecommendationToCart(recommendationCard, variantId, currentQuantity);
    }
  };

  /**
   * Adds the recommended product to cart and dispatches the theme cart update event.
   * Subscription cards include the selling plan and custom-shop properties so
   * the cart line renders as "Premium X Water - Subscription"; one-time cards
   * render as "Premium X Water - One Time".
   * @param {HTMLElement} recommendationCard - The recommendation card element.
   * @param {number} variantId - The variant id to add.
   * @param {number} quantity - The quantity to add.
   */
  #addRecommendationToCart(recommendationCard, variantId, quantity) {
    const addButton = recommendationCard.querySelector('[data-recommendation-action="add"]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    const productId = Number(recommendationCard.dataset.productId || 0);
    const purchaseType = recommendationCard.dataset.purchaseType === 'subscription' ? 'subscription' : 'one-time';
    const waterType = recommendationCard.dataset.waterType === 'sparkling' ? 'sparkling' : 'still';
    const sellingPlanId = Number(recommendationCard.dataset.sellingPlanId || 0) || null;
    const sellingPlanName = recommendationCard.dataset.sellingPlanName || '';
    const sectionIds = this.#getCartSectionIds();

    addButton.disabled = true;
    recommendationCard.classList.add('is-loading');

    const combination = this.#getRecommendationCombination(recommendationCard, variantId, quantity);

    this.#addRecommendationCombination(combination, { productId, purchaseType, waterType, sellingPlanId, sellingPlanName, sectionIds })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          throw new Error(response.message || 'Failed to add recommended product to cart');
        }

        // After adding, remove the shipping fee line item if the new total
        // box count meets the free-shipping threshold. The change response
        // sections include a re-rendered cart-summary, so use them when
        // available so the "Shipping: Free" update is reflected immediately.
        const shippingSections = await this.#removeShippingFeeIfFreeShipping();
        const sections = shippingSections || response.sections || {};

        recommendationCard.dispatchEvent(
          new CartAddEvent({}, String(variantId), {
            source: 'cart-recommendation',
            itemCount: quantity,
            variantId: String(variantId),
            sections,
          })
        );

        this.#clearStoredRecommendationQuantity(recommendationCard);
        this.#setRecommendationQuantity(recommendationCard, 2);
        this.#normalizeWaterLines();
      })
      .catch((error) => {
        console.error('Error adding recommended product:', error);
      })
      .finally(() => {
        addButton.disabled = false;
        recommendationCard.classList.remove('is-loading');
      });
  }

  /**
   * Returns all cart item section ids that should be re-rendered after a cart change.
   * @returns {string[]} The unique section ids.
   */
  #getCartSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionIds);
  }

  /**
   * Adds a recommendation to cart or merges it into an existing identical cart line.
   * Matching respects the one-time/subscription variation so different purchase
   * types stay as independent lines.
   * @param {object} config - The add config.
   * @param {number} config.variantId - The recommended variant id.
   * @param {number} config.productId - The product id.
   * @param {number} config.quantity - The quantity to add.
   * @param {string} config.purchaseType - "one-time" or "subscription".
   * @param {string} config.waterType - "still" or "sparkling".
   * @param {number|null} config.sellingPlanId - The selling plan id for subscriptions.
   * @param {string} config.sellingPlanName - The selling plan name.
   * @param {string[]} config.sectionIds - The section ids to re-render.
   * @returns {Promise<Response>} The cart API response.
   */
  async #addOrMergeRecommendationItem({ variantId, productId, quantity, unitBoxes = 1, purchaseType = 'one-time', waterType = 'still', sellingPlanId = null, sellingPlanName = '', sectionIds }) {
    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
    const mergeableItems = (cart.items || [])
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => this.#isMergeableRecommendationLine(item, variantId, productId, purchaseType));

    if (mergeableItems.length === 0) {
      return this.#addCartItems([this.#buildRecommendationCartItem({ variantId, quantity, unitBoxes, purchaseType, waterType, sellingPlanId, sellingPlanName })], sectionIds);
    }

    const [primaryItem, ...duplicateItems] = mergeableItems;
    const mergedQuantity = mergeableItems.reduce((total, entry) => total + Number(entry.item.quantity || 0), 0) + quantity;

    const primaryChangeResponse = await this.#changeCartLineQuantity(
      primaryItem.line,
      mergedQuantity,
      duplicateItems.length === 0 ? sectionIds : []
    );

    if (duplicateItems.length === 0) {
      return primaryChangeResponse;
    }

    for (const duplicate of duplicateItems.sort((left, right) => right.line - left.line)) {
      await this.#changeCartLineQuantity(duplicate.line, 0, []);
    }

    return this.#changeCartLineQuantity(primaryItem.line, mergedQuantity, sectionIds);
  }

  /**
   * Builds the /cart/add payload for a recommendation so the new line renders
   * with the same "Premium X Water - One Time / Subscription" labelling as
   * custom-shop lines in the drawer.
   * @param {object} config - The item config.
   * @returns {object} The cart API item.
   */
  #buildRecommendationCartItem({ variantId, quantity, unitBoxes = 1, purchaseType = 'one-time', waterType = 'still', sellingPlanId = null, sellingPlanName = '' }) {
    const normalizedPurchase = purchaseType === 'subscription' ? 'subscription' : 'one-time';
    const unitCount = Math.max(1, Number(quantity || 1));
    const boxesPerUnit = Math.max(1, Number(unitBoxes || 1));
    const shippingCents = normalizedPurchase === 'subscription' ? 0 : 867;
    const cartItem = {
      id: Number(variantId),
      quantity: unitCount,
      properties: {
        _custom_shop: 'true',
        _custom_shop_handle: waterType,
        _custom_shop_boxes: String(boxesPerUnit),
        _custom_shop_variant_label: `${boxesPerUnit} ${boxesPerUnit === 1 ? 'Box' : 'Boxes'} (${boxesPerUnit * this.#cansPerBox} cans)`,
        _custom_shop_shipping_cents: String(shippingCents),
        _custom_shop_free_shipping_threshold: '2',
        _custom_shop_purchase_type: normalizedPurchase === 'subscription' ? 'subscription' : 'one-time',
        _custom_shop_purchase_cadence: normalizedPurchase === 'subscription' ? 'Subscription' : 'One Time',
      },
    };

    if (sellingPlanName) {
      cartItem.properties._custom_shop_selling_plan = String(sellingPlanName);
    }

    if (normalizedPurchase === 'subscription' && Number(sellingPlanId) > 0) {
      cartItem.selling_plan = Number(sellingPlanId);
    }

    return cartItem;
  }

  /**
   * Adds items to cart.
   * @param {Array<object>} items - The items to add.
   * @param {string[]} sectionIds - The section ids to re-render.
   * @returns {Promise<Response>} The add response.
   */
  #addCartItems(items, sectionIds) {
    return fetch(`${Theme.routes.cart_add_url}`, {
      ...fetchConfig('json'),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
  }

  /**
   * Updates a single cart line quantity.
   * @param {number} line - The 1-indexed line number.
   * @param {number} quantity - The desired quantity.
   * @param {string[]} sectionIds - The section ids to re-render.
   * @returns {Promise<Response>} The change response.
   */
  #changeCartLineQuantity(line, quantity, sectionIds) {
    return fetch(`${Theme.routes.cart_change_url}`, {
      ...fetchConfig('json', {
        body: JSON.stringify({
          line,
          quantity,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      }),
    });
  }

  /**
   * Returns whether a cart line can be merged with a recommendation add.
   * The variant must match and the one-time/subscription purchase type must
   * match; other purchase types stay independent. Custom-shop bookkeeping
   * properties are allowed, other custom properties keep the line independent.
   * @param {object} item - The cart item.
   * @param {number} variantId - The target variant id.
   * @param {number} productId - The target product id.
   * @param {string} purchaseType - "one-time" or "subscription".
   * @returns {boolean} Whether the line can be merged.
   */
  #isMergeableRecommendationLine(item, variantId, productId, purchaseType = 'one-time') {
    const lineVariantId = Number(item?.variant_id || item?.id || 0);
    const lineProductId = Number(item?.product_id || 0);
    const properties = item?.properties || {};

    if (lineVariantId !== Number(variantId)) return false;
    if (productId && lineProductId && lineProductId !== Number(productId)) return false;
    if (properties._custom_shop_fee === 'shipping') return false;

    const hasBlockingProperties = Object.entries(properties).some(([key, value]) => {
      if (value === '' || value == null) return false;
      return !key.startsWith('_custom_shop') && !key.startsWith('_');
    });
    const hasOtherHiddenProperties = Object.entries(properties).some(([key, value]) => {
      if (value === '' || value == null) return false;
      return key.startsWith('_') && !key.startsWith('_custom_shop');
    });
    if (hasBlockingProperties || hasOtherHiddenProperties) return false;

    const lineIsSubscription =
      properties._custom_shop_purchase_type === 'subscription' ||
      item?.selling_plan_allocation != null;
    const targetIsSubscription = purchaseType === 'subscription';

    if (lineIsSubscription !== targetIsSubscription) return false;

    return true;
  }

  /**
   * Serializes a product's sized variants (id, boxes, availability) for a recommendation card.
   * @param {object} product - The Shopify product payload.
   * @returns {string} A JSON string.
   */
  #serializeRecommendationVariants(product) {
    const variants = (Array.isArray(product?.variants) ? product.variants : [])
      .map((variant) => ({
        id: Number(variant.id),
        boxes: this.#getVariantBoxCount(variant) || 0,
        available: variant.available !== false,
      }))
      .filter((variant) => variant.id && variant.boxes > 0);

    return JSON.stringify(variants);
  }

  /**
   * Splits a box count into the largest matching size variants (6, 4, 2, 1 boxes) so the
   * order reads "2 Boxes (16 Cans)" instead of "1 Box x 2".
   * @param {HTMLElement} recommendationCard - The recommendation card element.
   * @param {number} fallbackVariantId - The 1-box variant id.
   * @param {number} boxCount - The total boxes selected.
   * @returns {Array<{variantId: number, quantity: number, unitBoxes: number}>} The variants to add.
   */
  #getRecommendationCombination(recommendationCard, fallbackVariantId, boxCount) {
    const totalBoxes = Math.max(1, Number(boxCount || 1));
    const fallback = [{ variantId: Number(fallbackVariantId), quantity: totalBoxes, unitBoxes: 1 }];

    let variants = [];
    try {
      variants = JSON.parse(recommendationCard.dataset.variants || '[]');
    } catch (error) {
      return fallback;
    }

    return this.#splitIntoVariants(variants, totalBoxes, Number(fallbackVariantId)) || fallback;
  }

  /**
   * Splits a box count into the largest available size variants (for example 13 = 12 + 1).
   * @param {Array<{id: number, boxes: number, available: boolean}>} variants - The product's sized variants.
   * @param {number} totalBoxes - The total boxes wanted.
   * @param {number} fallbackVariantId - The variant to use as 1 box each when no split fits.
   * @returns {Array<{variantId: number, quantity: number, unitBoxes: number}>} The variants to have in the cart.
   */
  #splitIntoVariants(variants, totalBoxes, fallbackVariantId) {
    const sized = variants.filter((variant) => variant.available && variant.boxes > 0).sort((left, right) => right.boxes - left.boxes);
    const combination = [];
    let remainingBoxes = totalBoxes;

    sized.forEach((variant) => {
      const unitCount = Math.floor(remainingBoxes / variant.boxes);
      if (unitCount > 0) {
        combination.push({ variantId: Number(variant.id), quantity: unitCount, unitBoxes: variant.boxes });
        remainingBoxes -= unitCount * variant.boxes;
      }
    });

    if (remainingBoxes > 0 || combination.length === 0) {
      return [{ variantId: Number(fallbackVariantId), quantity: totalBoxes, unitBoxes: 1 }];
    }

    return combination;
  }

  /**
   * Adds each size variant of a combination in turn; only the last request re-renders sections.
   * @param {Array<{variantId: number, quantity: number, unitBoxes: number}>} combination - The variants to add.
   * @param {object} config - Shared add config.
   * @returns {Promise<Response>} The last cart API response.
   */
  async #addRecommendationCombination(combination, { productId, purchaseType, waterType, sellingPlanId, sellingPlanName, sectionIds }) {
    let response = null;

    for (const [index, part] of combination.entries()) {
      const isLast = index === combination.length - 1;
      response = await this.#addOrMergeRecommendationItem({
        variantId: part.variantId,
        productId,
        quantity: part.quantity,
        unitBoxes: part.unitBoxes,
        purchaseType,
        waterType,
        sellingPlanId,
        sellingPlanName,
        sectionIds: isLast ? sectionIds : [],
      });

      if (!isLast && !response.ok) return response;
    }

    return response;
  }

  /**
   * Finds the recommended variant, preferring the 1-box option.
   * @param {object} product - The Shopify product payload.
   * @param {number|null} preferredVariantId - A Liquid-provided 1-box variant id.
   * @returns {object | null} The selected variant.
   */
  #getRecommendationVariant(product, preferredVariantId = null) {
    if (!Array.isArray(product?.variants) || product.variants.length === 0) return null;

    if (Number(preferredVariantId) > 0) {
      const preferred = product.variants.find((variant) => Number(variant.id) === Number(preferredVariantId));
      if (preferred) return preferred;
    }

    return (
      product.variants.find((variant) => this.#getVariantBoxCount(variant) === 1) ||
      product.variants[0]
    );
  }

  /**
   * Extracts the box count from a cart item.
   * @param {object | null} item - The cart item.
   * @returns {number | null} The parsed box count.
   */
  #getCartItemBoxCount(item) {
    if (!item) return null;

    const props = item.properties || {};
    const quantity = Number(item.quantity || 1);
    if (props._custom_shop === 'true' && props._custom_shop_fee !== 'shipping') {
      return quantity * (Number(props._custom_shop_boxes || 0) || 1);
    }

    const boxesPerUnit =
      Number(props._custom_shop_boxes || 0) ||
      this.#extractBoxCount(
        item.variant_title ||
          item.options_with_values?.map((option) => option.value).join(' ') ||
          item.title ||
          ''
      ) ||
      1;

    return boxesPerUnit * quantity;
  }

  /**
   * Extracts the box count from a product variant.
   * @param {object} variant - The product variant.
   * @returns {number | null} The parsed box count.
   */
  #getVariantBoxCount(variant) {
    return this.#extractBoxCount(variant?.public_title || variant?.title || '');
  }

  /**
   * Parses a box count from the start of a label like "2 Cases (16 Cans)".
   * @param {string} value - The source label.
   * @returns {number | null} The parsed box count.
   */
  #extractBoxCount(value) {
    const match = String(value || '').match(/(\d+)\s*(case|cases|box|boxes)\b/i);
    return match ? Number(match[1]) : null;
  }

  /**
   * Builds the title text for the recommendation card.
   * The purchase type renders on its own line via `.cart-items__purchase-type`,
   * without a dash separator.
   * @param {object} product - The Shopify product payload.
   * @param {string} purchaseType - "one-time" or "subscription".
   * @returns {string} The display title.
   */
  #getRecommendationTitle(product, purchaseType = 'one-time') {
    const title = product.title || '';
    const suffix = purchaseType === 'subscription' ? 'Subscription (Every 2 weeks)' : 'One Time';
    return `${title}\n${suffix}`;
  }

  /**
   * Formats a "{boxes} box(es) ({cans} cans)" label.
   * @param {number} boxCount - The total boxes.
   * @returns {string} The formatted label.
   */
  #formatBoxCanLabel(boxCount) {
    const totalBoxes = Math.max(1, Number(boxCount || 1));
    const totalCans = totalBoxes * this.#cansPerBox;
    return `${totalBoxes} box${totalBoxes === 1 ? '' : 'es'} (${totalCans} cans)`;
  }

  /**
   * Returns note copy for products that need extra context in the drawer.
   * @param {object} product - The Shopify product payload.
   * @returns {string} Optional note text.
   */
  #getRecommendationNote(product) {
    if (product.handle?.includes('sparkling')) {
      return 'Sparkling water available on backorder';
    }

    return '';
  }

  /**
   * After the recommendation card item has been added to the cart, checks
   * whether the total box count now makes the shipping-fee placeholder line
   * unnecessary. When the cart reaches the free-shipping threshold, or when
   * no qualifying water items remain, remove that placeholder so the rendered
   * cart state matches checkout.
   *
   * Counts boxes from both:
   *  - Custom-shop items (quantity equals the number of boxes)
   *  - Regular water items added via the recommendation card (no properties)
   *
   * @returns {Promise<object|null>} The sections payload from the cart/change
   *   response (used to morph the cart summary), or null if no change was made.
   */
  async #removeShippingFeeIfFreeShipping() {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());

      let threshold = 2; // default; overridden from item properties when present
      let shippingLineIndex = null;
      let totalBoxes = 0;

      cart.items.forEach((item, index) => {
        const props = item.properties || {};

        // Locate the shipping-fee placeholder line item
        if (props._custom_shop_fee === 'shipping') {
          shippingLineIndex = index + 1; // cart/change uses 1-indexed lines
          return;
        }

        // Custom-shop water items store how many boxes one unit holds, so the
        // line's boxes are that times its quantity.
        if (props._custom_shop === 'true') {
          totalBoxes += Number(item.quantity || 0) * (Number(props._custom_shop_boxes || 0) || 1);
          const itemThreshold = Number(props._custom_shop_free_shipping_threshold || 0);
          if (itemThreshold > 0) threshold = itemThreshold;
          return;
        }

        // Regular water items added via the recommendation card have no
        // _custom_shop properties — derive box count from the variant title
        const title = (item.product_title || '').toLowerCase();
        if (title.includes('still water') || title.includes('sparkling water')) {
          const boxCount = this.#extractBoxCount(
            item.variant_title ||
              item.options_with_values?.map((o) => o.value).join(' ') ||
              item.title ||
              ''
          );
          totalBoxes += (boxCount || 1) * item.quantity;
        }
      });

      if (shippingLineIndex === null) return null;
      if (totalBoxes > 0 && totalBoxes < threshold) return null;

      // Remove the shipping fee line item and request re-rendered sections
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
        .filter(Boolean);

      const changeResponse = await fetch(
        `${Theme.routes.cart_change_url}`,
        fetchConfig('json', {
          body: JSON.stringify({
            line: shippingLineIndex,
            quantity: 0,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        })
      ).then((r) => r.json());

      return changeResponse.sections || null;
    } catch (error) {
      console.error('Error checking/removing shipping fee:', error);
      return null;
    }
  }

  /**
   * Counts the total number of boxes across all cart line items.
   * Each item's box count (from its variant title) is multiplied by its quantity.
   * @param {Array} cartItems - The cart items array.
   * @returns {number} The total box count.
   */
  #getTotalBoxCount(cartItems) {
    let total = 0;
    cartItems.forEach((item) => {
      const props = item.properties || {};
      if (props._custom_shop_fee === 'shipping') return;

      if (props._custom_shop === 'true') {
        total += Number(item.quantity || 1) * (Number(props._custom_shop_boxes || 0) || 1);
        return;
      }

      const boxCount =
        Number(props._custom_shop_boxes || 0) ||
        this.#extractBoxCount(
          item.variant_title ||
            item.options_with_values?.map((o) => o.value).join(' ') ||
            item.title ||
            ''
        ) ||
        1;

      total += boxCount * Number(item.quantity || 1);
    });
    return total;
  }

  /**
   * Returns the minimum selectable quantity for a recommendation card.
   * Subscription cards require at least 2 boxes; one-time cards allow 1.
   * @param {object | HTMLElement} source - A product payload or recommendation card element.
   * @param {string} purchaseType - "one-time" or "subscription".
   * @returns {number} The minimum quantity.
   */
  #getRecommendationMinQuantity(source, purchaseType = 'one-time') {
    if (source instanceof HTMLElement) {
      return source.dataset.purchaseType === 'subscription' ? 2 : 1;
    }

    return purchaseType === 'subscription' ? 2 : 1;
  }

  /**
   * Gets the current recommendation quantity from the DOM.
   * @param {HTMLElement} recommendationCard - The recommendation card element.
   * @returns {number} The selected quantity.
   */
  #getRecommendationQuantity(recommendationCard) {
    const minQuantity = this.#getRecommendationMinQuantity(recommendationCard);
    const quantityInput = recommendationCard.querySelector('[data-recommendation-quantity-input]');
    const quantityElement = recommendationCard.querySelector('[data-recommendation-quantity]');
    const quantity = Number(quantityInput?.value || quantityElement?.textContent || 2);
    return Math.min(99, Math.max(minQuantity, quantity || 2));
  }

  /**
   * Sets the current recommendation quantity and updates the displayed price.
   * @param {HTMLElement} recommendationCard - The recommendation card element.
   * @param {number} quantity - The new quantity.
   */
  #setRecommendationQuantity(recommendationCard, quantity) {
    const minQuantity = this.#getRecommendationMinQuantity(recommendationCard);
    const clampedQuantity = Math.min(99, Math.max(minQuantity, quantity));
    const quantityInput = recommendationCard.querySelector('[data-recommendation-quantity-input]');
    const quantityElement = recommendationCard.querySelector('[data-recommendation-quantity]');
    const totalPriceElement = recommendationCard.querySelector('[data-recommendation-total-price]');
    const linePriceElement = recommendationCard.querySelector('[data-recommendation-line-price]');
    const boxCountElement = recommendationCard.querySelector('[data-recommendation-box-count]');
    const unitBreakdownElement = recommendationCard.querySelector('[data-recommendation-unit-breakdown]');
    const cansBreakdownElement = recommendationCard.querySelector('[data-recommendation-can-breakdown]');
    const packBoxesElement = recommendationCard.querySelector('[data-recommendation-pack-boxes]');
    const packCansElement = recommendationCard.querySelector('[data-recommendation-pack-cans]');
    const unitPrice = Number(recommendationCard.dataset.unitPrice || 0);
    const formattedUnit = this.#formatRecommendationPrice(unitPrice);
    const formattedTotal = this.#formatRecommendationPrice(unitPrice * clampedQuantity);

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(clampedQuantity);
    }

    if (quantityElement instanceof HTMLElement) {
      quantityElement.textContent = String(clampedQuantity);
    }

    if (totalPriceElement instanceof HTMLElement) {
      totalPriceElement.textContent = formattedTotal;
    }

    if (linePriceElement instanceof HTMLElement) {
      linePriceElement.textContent = formattedTotal;
    }

    if (boxCountElement instanceof HTMLElement) {
      boxCountElement.textContent = this.#formatBoxCanLabel(clampedQuantity);
    }

    if (unitBreakdownElement instanceof HTMLElement) {
      unitBreakdownElement.textContent = `${formattedUnit} x ${clampedQuantity} ${clampedQuantity === 1 ? 'Box' : 'Boxes'}`;
    }

    if (cansBreakdownElement instanceof HTMLElement) {
      cansBreakdownElement.textContent = `(${clampedQuantity * this.#cansPerBox} cans)`;
    }

    if (packBoxesElement instanceof HTMLElement) {
      packBoxesElement.textContent = `${clampedQuantity} ${clampedQuantity === 1 ? 'Box' : 'Boxes'}`;
    }

    if (packCansElement instanceof HTMLElement) {
      packCansElement.textContent = `(${clampedQuantity * this.#cansPerBox} Cans)`;
    }

    this.#storeRecommendationQuantity(recommendationCard, clampedQuantity);
  }

  /**
   * Returns the persisted recommendation quantity for a product, defaulting to 2.
   * @param {object | HTMLElement} source - A product payload or recommendation card element.
   * @param {string} purchaseType - "one-time" or "subscription".
   * @returns {number} The stored quantity.
   */
  #getStoredRecommendationQuantity(source, purchaseType = 'one-time') {
    const minQuantity = this.#getRecommendationMinQuantity(source, purchaseType);
    const stateKey = this.#getRecommendationQuantityStateKey(source, purchaseType);
    const storedQuantity = stateKey ? Number(this.#recommendationQuantities.get(stateKey)) : 0;
    return Math.min(99, Math.max(minQuantity, storedQuantity || 2));
  }

  /**
   * Persists the current recommendation quantity so cart updates do not overwrite it.
   * @param {object | HTMLElement} source - A product payload or recommendation card element.
   * @param {number} quantity - The quantity to persist.
   */
  #storeRecommendationQuantity(source, quantity) {
    const minQuantity = this.#getRecommendationMinQuantity(source);
    const stateKey = this.#getRecommendationQuantityStateKey(source);
    if (!stateKey) return;

    this.#recommendationQuantities.set(stateKey, Math.min(99, Math.max(minQuantity, Number(quantity || 2))));
  }

  /**
   * Clears any stored recommendation quantity for a product.
   * @param {object | HTMLElement} source - A product payload or recommendation card element.
   */
  #clearStoredRecommendationQuantity(source) {
    const stateKey = this.#getRecommendationQuantityStateKey(source);
    if (!stateKey) return;

    this.#recommendationQuantities.delete(stateKey);
  }

  /**
   * Builds the internal quantity-state key for a recommendation product.
   * Includes the purchase type so one-time and subscription cards for the
   * same product keep independent quantities.
   * @param {object | HTMLElement} source - A product payload or recommendation card element.
   * @param {string} purchaseType - "one-time" or "subscription".
   * @returns {string} The state key.
   */
  #getRecommendationQuantityStateKey(source, purchaseType = 'one-time') {
    const normalizedPurchase = purchaseType === 'subscription' ? 'subscription' : 'one-time';

    if (source instanceof HTMLElement) {
      const baseKey = source.dataset.productId || source.dataset.productHandle || source.dataset.variantId || '';
      const cardPurchase = source.dataset.purchaseType || normalizedPurchase;
      return baseKey ? `${baseKey}:${cardPurchase}` : '';
    }

    const baseKey = String(source?.id || source?.handle || source?.variants?.[0]?.id || '');
    return baseKey ? `${baseKey}:${normalizedPurchase}` : '';
  }

  /**
   * Formats a product price in cents for display.
   * @param {number | string} priceInCents - The product price in cents.
   * @returns {string} The formatted price.
   */
  #formatRecommendationPrice(priceInCents) {
    const amount = Number(priceInCents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      }).format(amount);
    } catch (error) {
      console.error('Error formatting recommendation price:', error);
      return `$${amount.toFixed(2)}`;
    }
  }

  /**
   * Escapes HTML-sensitive characters in strings rendered into the DOM.
   * @param {string} value - The raw string.
   * @returns {string} The escaped string.
   */
  #escapeHtml(value) {
    const escapeMap = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return String(value).replace(/[&<>"']/g, (character) => escapeMap[character]);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    this.#lastCart = null;
    this.#normalizeWaterLines();

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphKeepingCards(this.sectionId, cartItemsHtml);
      // After morphing, fetch cart data and update recommendations
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => this.#updateMoreItemsSection(cart.items))
        .catch(error => console.error('Error fetching cart data:', error));
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
